import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import type { ModalContextType } from "@/components/core/modals/types";
import { useModal } from "@/components/core/modals/use-modal.ts";
import Tooltip from "@/components/core/tooltip";
import { FileViewer } from "@/components/document-viewer/file-viewer.tsx";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import type { Media } from "@/features/dashboard/types/media.ts";
import { downloadFile } from "@/utils/download-file";
import { formatDate } from "@/utils/format-date.ts";
import { formatFileSize } from "@/utils/format-file-size";
import { formatNumber } from "@/utils/format-number.ts";
import { IconDatabaseExclamation, IconDownload, IconEye, IconFileTypePdf, IconInfoSmall, IconPlus, IconPrinter, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import CaseFileUploadModal from "../case-classification/case-file-upload-modal";
import { useCaseRecordAnnexDeleteMutation, useCaseRecordAnnexMutation } from "../case-record/api/queries/case-records-query";
import type { CaseRecord } from "../case-record/types/case-records";

interface FileUploadButtonProps {
	modal: ModalContextType;
	data: CaseRecord;
	setFiles: React.Dispatch<React.SetStateAction<File[]>>;
	files: File[];
}

function FileUploadButton({ modal, data }: FileUploadButtonProps) {
	const [files, setFiles] = useState<File[]>([]);
	const attachmentsMutation = useCaseRecordAnnexMutation();

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(event.target.files || []);
		if (selectedFiles.length > 0) {
			const updatedFiles = [...files, ...selectedFiles];
			setFiles(updatedFiles);

			const mediaModal = modal.openModal({
				title: "Upload Case Complaint Documents",
				size: "lg",
				showHeader: true,
				showFooter: false,
				component: (
					<CaseFileUploadModal
						onSubmit={async (value) => {
							await attachmentsMutation.mutateAsync({
								id: data.id,
								files: value,
							});
							setFiles([]);
							modal.closeModal(mediaModal);
						}}
						initialFiles={updatedFiles}
					/>
				),
			});
		}
	};

	return (
		<Button className="relative" type="button" size="sm">
			<input type="file" multiple className="absolute opacity-0 inset-0" onChange={handleFileChange} />
			<IconPlus size={14} stroke={2} />
			<span className="">Add File</span>
		</Button>
	);
}

export default function CaseAnnexesTable({
	title = "media Receipts",
	data,
	allowAdd = false,
	allowRemove = true,
	allowPreview = true,
	allowPrint = false,
	allowDownload = false,
}: {
	title: string;
	data: CaseRecord;
	counts: number;
	allowRemove: boolean;
	allowAdd: boolean;
	allowPreview: boolean;
	allowPrint: boolean;
	allowDownload: boolean;
}) {
	const modal = useModal();
	const documentTokenMutation = useDocumentConfigMutation();
	const attachmentDeleteMutation = useCaseRecordAnnexDeleteMutation();

	const handleDownload = (attachment: Media) => {
		downloadFile(attachment.original_url, attachment.file_name);
	};

	const handleClick = async (attachment: Media) => {
		const fileExtension = attachment.file_name.split(".").pop();

		let response = null;

		if (
			attachment.mime_type === "application/vnd.ms-excel" ||
			attachment.mime_type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
			attachment.mime_type?.startsWith("audio/")
		) {
			return false;
		}

		if (!["image/jpeg", "image/png", "png", "jpeg", "video/mp4", "mp4"].includes(fileExtension)) {
			response = await documentTokenMutation.mutateAsync({
				file_type: fileExtension,
				title: attachment.name,
				url: attachment.original_url,
				mode: "view",
			});
		}

		modal.openModal({
			title: "Default Modal",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} token={response ? response?.token : null} options={response ? response?.options : null} />,
		});
	};
	const [files, setFiles] = useState<File[]>([]);

	const handleRemove = async (caseRecordId: string, mediaId: string) => {
		await attachmentDeleteMutation.mutateAsync({ caseRecordId, mediaId });
		modal.closeModal(m);
		// const m = modal.openModal({
		//   type: "confirm",
		//   title: "Confirm Archive",
		//   variant: "red",
		//   description: "Are you sure you want to remove this media?",
		//   onConfirm: async () => {
		//     await attachmentDeleteMutation.mutateAsync({ caseRecordId, mediaId });
		//     modal.closeModal(m);
		//   },
		// });
	};

	const caseDocumentsColumns: ColumnDef<FileRecord>[] = [
		{
			id: "name",
			label: "File Name",
			cellClassName: "",
			cell: ({ data }) => (
				<div className="flex items-center">
					<button
						type="button"
						onClick={() => handleClick(data.medias[0])}
						className={
							data.medias[0]?.custom_properties?.is_amend === true
								? "font-semibold text-red-600 hover:text-blue-500 line-through"
								: "font-semibold text-blue-600 hover:text-blue-500"
						}
					>
						{data.medias[0]?.name}
					</button>
				</div>
			),
		},
		{
			id: "mime_type",
			label: "File Type",
			cellClassName: "",
			cell: ({ data }) => data.medias[0]?.mime_type,
		},
		{
			id: "size",
			label: "File Size",
			cellClassName: "",
			cell: ({ data }) => <span>{formatFileSize(data.medias[0]?.size)}</span>,
		},
		{
			id: "date_created",
			label: "Date Created",
			cellClassName: "text-right",
			headerClassName: "text-right",
			cell: ({ data }) => <span>{formatDate(data.created_at, "PPp")}</span>,
		},
	];
	return (
		<div className="flex flex-col w-full rounded-md overflow-hidden">
			<div className="flex flex-row items-center justify-between w-full p-4 gap-4 bg-slate-50">
				<span className="text-base font-semibold">{title}</span>
				{allowAdd && <FileUploadButton modal={modal} data={data} files={files} setFiles={setFiles} />}
			</div>

			{data?.annexes?.length !== 0 ? (
				<div className="flex flex-col w-full gap-2 pb-4">
					<DataTable
						className="border-collapse"
						data={data?.annexes}
						columns={caseDocumentsColumns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
						onDelete={(media) => allowRemove && handleRemove(media.id!, media.id)}
					/>
				</div>
			) : (
				<Blanket width="sm">
					<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
					<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
				</Blanket>
			)}
		</div>
	);
}
